import { FolderDropTarget } from "@/dnd/FolderDropTarget";
import { droppableId } from "@/dnd/ids";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { CanvasFolderDropTarget } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasFolderTree";
import { ChatFolderDropTarget } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailChatFolderTree";
import {
  buildFileRecordKind,
  ObjectActionCardMenu,
  useResolvedFileRecordActions,
} from "@/object-actions";
import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import { cn } from "@/utils";
import {
  FOLDER_RESOURCE_TYPE_CANVAS,
  type FileRecord,
  type Folder,
} from "@cohesive/api-client";
import { formatDistanceToNow } from "date-fns";
import { ChevronRight, Folder as FolderIcon, FolderOpen } from "lucide-react";
import React, { useMemo } from "react";
import { RowOpenPill } from "../components/RowOpenPill";
import {
  useResourceFolderActions,
  type ResourceFolderActionDeps,
} from "../components/WorkspaceResourceFolderCardItem";
import { BrowserLocationCell } from "../home/browser/BrowserLocationCell";
import { BROWSER_LIST_COL } from "../home/browser/browserListColumns";
import { getItemName } from "../types";

interface BrowserFolderRowProps {
  name: string;
  childCount: number;
  open: boolean;
  onToggle: () => void;
  /** Drill into the folder (double-click), like the grid folder cards. */
  onOpen?: () => void;
  /** Tree depth for indentation (0 = top level). */
  depth: number;
  /** Last-modified timestamp for the Modified column ("—" when absent). */
  modifiedAt?: string;
  /** Owning workspace for the Location column ("—" when absent). */
  workspaceId?: string;
  menuSlot?: React.ReactNode;
  /** The kebab menu's open state — holds the timestamp→actions swap while
   *  the menu is up (the pointer leaves the row for the portaled menu). */
  menuOpen?: boolean;
  onContextMenu?: (e: React.MouseEvent) => void;
}

/**
 * Inline-expandable folder row for the workspace browser's list view, in the
 * shared `BROWSER_LIST_COL` geometry: chevron+icon tile · name · workspace
 * (Location column, wide mode only) · "Folder" (Type column, wide mode
 * only) · child count (Size column) · modified (which swaps to the Open
 * pill + kebab on hover).
 * A single
 * click (or Space) toggles expansion in place, like the left rail's folder
 * rows; a double click (or Enter) — or the Open pill — drills into the
 * folder, matching the grid's folder cards. Folder rows don't participate
 * in selection — expansion is their click gesture.
 */
export function BrowserFolderRow({
  name,
  childCount,
  open,
  onToggle,
  onOpen,
  depth,
  modifiedAt,
  workspaceId,
  menuSlot,
  menuOpen,
  onContextMenu,
}: BrowserFolderRowProps) {
  const modified = modifiedAt
    ? formatDistanceToNow(new Date(modifiedAt), { addSuffix: true }).replace(
        /^about /,
        "",
      )
    : "—";
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onDoubleClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" && onOpen) {
          e.preventDefault();
          onOpen();
        } else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      onContextMenu={onContextMenu}
      className={cn(
        /* -ml + width comp: the leading gutter hangs 6px into the page
           padding while the trailing edge stays aligned with the toolbar. */
        "group -ml-1.5 flex min-h-12 w-[calc(100%+6px)] cursor-pointer select-none items-center gap-3 rounded-md py-1.5 pr-2.5 text-left outline-none transition-colors",
        /* Zebra base + n-10 hover — same treatment as `HomeBrowserListRow`
           so folder and item rows stripe/hover as one list. */
        "bg-[var(--row-stripe,transparent)]",
        "hover:bg-n-10 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
      )}
      style={depth > 0 ? { paddingLeft: `${depth * 20}px` } : undefined}
    >
      {/* Expand chevron in a slim leading gutter, then the folder glyph in
          the same 40px square tile as the item rows' previews. The -mr-1.5
          halves the row's gap-3 after the gutter so the indent stays modest;
          item rows carry a matching blank gutter to keep tiles in one rail. */}
      <span
        aria-hidden
        className="-mr-1.5 flex w-3.5 shrink-0 items-center justify-center"
      >
        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-n-40 transition-transform duration-150 ease-out",
            open && "rotate-90",
          )}
        />
      </span>
      <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-n-05 text-n-50">
        {open ? (
          <FolderOpen className="size-4 shrink-0" />
        ) : (
          <FolderIcon className="size-4 shrink-0" />
        )}
      </span>

      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
        {name}
      </span>

      <BrowserLocationCell workspaceId={workspaceId} />

      <span
        className={cn(
          BROWSER_LIST_COL.type,
          "truncate text-xs text-muted-foreground",
        )}
      >
        Folder
      </span>

      <span
        className={cn(
          BROWSER_LIST_COL.size,
          "truncate text-xs text-muted-foreground",
        )}
      >
        {childCount === 0
          ? "—"
          : `${childCount} item${childCount === 1 ? "" : "s"}`}
      </span>

      {/* Trailing cell: the timestamp at rest, swapped for the Open pill +
          kebab on hover — same treatment as `HomeBrowserListRow`. */}
      <span className={cn(BROWSER_LIST_COL.modified, "flex items-center")}>
        <span
          className={cn(
            "truncate text-xs text-muted-foreground",
            menuOpen ? "hidden" : "group-hover:hidden",
          )}
        >
          {modified}
        </span>
        {/* Left-justified so the pill lines up with the timestamp it replaces. */}
        <span
          className={cn(
            "w-full items-center gap-1",
            menuOpen ? "flex" : "hidden group-hover:flex",
          )}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {onOpen && <RowOpenPill label={name} onOpen={onOpen} />}
          {menuSlot}
        </span>
      </span>
    </div>
  );
}

/** Canvas/chat folder row — registry menu via `useResourceFolderActions`.
 *  The header row doubles as a move drop target (same wrappers as the grid
 *  folder cards and crumbs); expanded children render as siblings, so a drag
 *  *out* of the folder isn't recaptured by it. */
export function ResourceBrowserFolderRow({
  folder,
  workspaceId,
  childCount,
  open,
  onToggle,
  depth,
  deps,
}: {
  folder: Folder;
  workspaceId: string;
  childCount: number;
  open: boolean;
  onToggle: () => void;
  depth: number;
  deps: ResourceFolderActionDeps;
}) {
  const controller = useRailRowContextMenu();
  const { kind, actions } = useResourceFolderActions(
    folder,
    controller.open,
    deps,
  );

  const isCanvas = folder.resource_type === FOLDER_RESOURCE_TYPE_CANVAS;
  const DropTarget = isCanvas ? CanvasFolderDropTarget : ChatFolderDropTarget;
  const dropIdFor = isCanvas
    ? droppableId.detailCanvasFolder
    : droppableId.detailChatFolder;

  return (
    <DropTarget
      workspaceId={workspaceId}
      folderId={folder.id}
      /* `:row` keeps the id distinct from the grid card and crumb mounts of
         the same folder — dnd-kit ids must be unique. */
      dropId={`${dropIdFor(workspaceId, folder.id)}:row`}
      className="rounded-md"
    >
      <BrowserFolderRow
        name={folder.name}
        childCount={childCount}
        open={open}
        onToggle={onToggle}
        onOpen={deps.onOpen}
        depth={depth}
        modifiedAt={folder.updated_at}
        workspaceId={workspaceId}
        menuOpen={controller.open}
        onContextMenu={controller.onContextMenu}
        menuSlot={
          <ObjectActionCardMenu
            controller={controller}
            actions={actions}
            kind={kind}
            name={folder.name}
          />
        }
      />
    </DropTarget>
  );
}

/** File-record folder row — registry menu via the file-record manifest.
 *  Wrapped in the same `FolderDropTarget` as the grid folder cards so
 *  dropping items on the header row moves them into the folder. */
export function FileBrowserFolderRow({
  record,
  workspaceId,
  childCount,
  open,
  onToggle,
  depth,
  handlerDeps,
}: {
  record: FileRecord;
  workspaceId: string;
  childCount: number;
  open: boolean;
  onToggle: () => void;
  depth: number;
  handlerDeps: FileRecordActionHandlerDeps;
}) {
  const controller = useRailRowContextMenu();
  const kind = useMemo(() => buildFileRecordKind(record), [record]);
  const { actions } = useResolvedFileRecordActions({
    record,
    surface: "main-view",
    enabled: controller.open,
    // Favorite/copy visibility is registry-scoped (see manifests/fileRecord),
    // not a per-callsite flag — folders get the same action set as files.
    handlerDeps,
  });

  return (
    <FolderDropTarget
      folderPath={record.path}
      workspaceId={workspaceId}
      className="rounded-md"
    >
      <BrowserFolderRow
        name={getItemName(record)}
        childCount={childCount}
        open={open}
        onToggle={onToggle}
        onOpen={
          handlerDeps.onOpen ? () => handlerDeps.onOpen?.(record) : undefined
        }
        depth={depth}
        modifiedAt={record.last_modified_at ?? record.updated_at}
        workspaceId={record.workspace_id ?? workspaceId}
        menuOpen={controller.open}
        onContextMenu={controller.onContextMenu}
        menuSlot={
          <ObjectActionCardMenu
            controller={controller}
            actions={actions}
            kind={kind}
            name={getItemName(record)}
          />
        }
      />
    </FolderDropTarget>
  );
}
